import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ENVIRONMENT_PLATFORM_TOKEN, ENVIRONMENT_WEBSITE_TOKEN } from '@campus/environment';
import { StringFunctions } from '@campus/utils';
import '@diekeure/dcr-components/dcr-card.js';
import '@diekeure/dcr-components/dcr-image.js';
import '@diekeure/dcr-components/dcr-inline-icon-button.js';
import '@diekeure/dcr-components/dcr-section.js';
import '@diekeure/dcr-components/dcr-tonal-icon-button.js';
import { combineLatest, fromEvent, Observable, Subject, timer } from 'rxjs';
import { delay, filter, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { FaqCategoryInterface, FaqInterface, ScrollToFragmentService } from '../../services';
import { RoleType, VideoPlayerType } from '../../types';
import { FaqViewModel, SelectedQuestionInterface } from './faq.viewmodel';

@Component({
  standalone: false,
  selector: 'campus-faq-page',
  templateUrl: './faq-page.component.html',
  styleUrls: ['./faq-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FaqViewModel],
})
export class FaqPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class')
  defaultClasses = ['faq-page'];

  public selectedRole$: Observable<RoleType>;
  public selectedQuestions$: Observable<SelectedQuestionInterface>;
  public filteredFaqCategories$: Observable<FaqCategoryInterface[]>;
  public windowScrolled$: Observable<boolean>;
  public isMaintenanceMode$: Observable<boolean>;
  public statusPageUrl: string;
  public videoPlayer: VideoPlayerType = 'wistia';
  public currentSlug$: Observable<string>;

  private viewModel = inject<FaqViewModel>(FaqViewModel);
  private router = inject<Router>(Router);
  private route = inject<ActivatedRoute>(ActivatedRoute);
  private environmentWebsite = inject(ENVIRONMENT_WEBSITE_TOKEN);
  private platform = inject(ENVIRONMENT_PLATFORM_TOKEN);
  private scrollToFragmentService = inject(ScrollToFragmentService);

  private title = inject(Title);

  private _destroy$ = new Subject<void>();
  public copied$ = new Subject<string>();
  public searchTerm: string;

  ngOnInit() {
    this.setupStreams();
    this.statusPageUrl = this.environmentWebsite.statusPageUrl;
    this.currentSlug$ = this.viewModel.slug$;

    this.viewModel.role$.pipe(takeUntil(this._destroy$)).subscribe((role) => {
      let forType = '';
      switch (role) {
        case 'student':
          forType = 'voor leerlingen';
          break;
        case 'teacher':
          forType = 'voor leerkrachten';
          break;
        case 'schooladmin':
          forType = 'voor schoolbeheerders';
          break;
      }
      this.title.setTitle(`${StringFunctions.capitalizeFirstLetter(this.platform)} - Veelgestelde vragen ${forType}`);
    });

    this.initScrollToSlug();
  }

  ngAfterViewInit(): void {
    const shell = document.getElementsByTagName('dcr-app-shell')[0];
    const main = shell?.shadowRoot!.querySelector('main');

    if (main) {
      this.windowScrolled$ = fromEvent(main, 'scroll').pipe(
        takeUntil(this._destroy$),
        map((e) => (e.target as HTMLElement)?.scrollTop > 100)
      );
    }
  }
  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private setupStreams(): void {
    this.selectedRole$ = this.viewModel.role$;
    this.filteredFaqCategories$ = this.viewModel.faqCategories$;

    this.selectedQuestions$ = this.viewModel.selectedQuestions$.pipe(startWith({}));
    this.isMaintenanceMode$ = this.viewModel.isMaintenanceMode$;
  }

  public roleClicked(role: RoleType): void {
    this.router.navigate(['.'], { relativeTo: this.route, queryParams: { role: role } });
  }

  public categoryClicked(category: FaqCategoryInterface): void {
    this.scrollToFragmentService.scrollToFragment(category.slug).subscribe();
  }

  public trackById(index: number, category: FaqCategoryInterface): number {
    return category.id;
  }
  public trackBySlug(index: number, question: FaqInterface) {
    return question.slug;
  }

  public scrollToTop(): void {
    const shell = document.getElementsByTagName('dcr-app-shell')[0];
    const main = shell?.shadowRoot!.querySelector('main');

    main?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  public copyLink(slug: string, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();

    this.viewModel.copyLink(slug);

    this.copied$.next(slug);
    timer(2000).subscribe(() => this.copied$.next(undefined));
  }

  public changeSearchTerm(event: InputEvent) {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;

    this.filteredFaqCategories$;
  }

  private initScrollToSlug() {
    combineLatest([this.viewModel.faqCategories$, this.viewModel.slug$])
      .pipe(
        filter(([categories, slug]) => {
          return categories.length > 0 && slug !== undefined;
        }),
        take(1),
        delay(100),
        switchMap(([_, slug]) => this.scrollToFragmentService.scrollToFragment(slug))
      )
      .subscribe();
  }
}
